import type { PropsWithChildren } from "react";
import {
  useModuleBEM,
  useTenantImages,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents, Funnel } from "@b2bportal/core-types";
import type { PageLoadingScreenComponentProps as CorePageLoadingScreenComponentProps } from "@b2bportal/core-ui";
import { ensureExhaustive, useGetCurrentFunnel } from "@overrides/utilities";
import { Dialog, DialogContent, Loading } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./PageLoadingScreen.module.scss";

export interface PageLoadingScreenComponentProps
  extends CorePageLoadingScreenComponentProps {
  subtitle?: string;
}

export const PageLoadingScreen = ({
  isLoading = false,
  className,
  text = "",
  subtitle = "",
}: PropsWithChildren<PageLoadingScreenComponentProps>) => {
  const styles = useUiStyles(CoreUiComponents.PageLoadingScreen, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreUiComponents.PageLoadingScreen);

  const funnel = useGetCurrentFunnel();

  const images = useTenantImages();

  const imageSrc = (() => {
    switch (funnel) {
      case Funnel.Ground:
        return images.carsHomepageImage;
      case Funnel.Activities:
        return images.activitiesHomepageImage;
      case Funnel.Lodging:
        return images.hotelLoadingImage;
      case Funnel.Air:
      case Funnel.Package:
      case Funnel.Trips:
        return images.flightLoadingImage;
      default:
        return ensureExhaustive(funnel);
    }
  })();

  return isLoading ? (
    <Dialog open={isLoading}>
      <DialogContent
        showCloseButton={false}
        header={null}
        fullScreen
        className={clsx(block, className)}
        title={text}
        titleIsVisuallyHidden
      >
        <div className={cn("container")}>
          <div className={cn("content")}>
            {/* Announce the loading message to screen readers (WCAG 4.1.3). */}
            <div
              className={cn("text-container")}
              role="status"
              aria-live="polite"
            >
              <Loading />
              {text && <div className={cn("text")}>{text}</div>}
              {subtitle && <div className={cn("subtitle")}>{subtitle}</div>}
            </div>
          </div>
          <div className={cn("image-container")}>
            <img src={imageSrc} alt="" className={cn("image")} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;
};
